import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Rocket, Play } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { resolveCtaTarget } from "@/lib/ctaTarget";

export const Hero = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const ctaTarget = resolveCtaTarget(user);
    const scrollToHow = () => {
        const el = document.querySelector('[data-testid="how-it-works-section"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-screen flex flex-col justify-center pt-32 pb-16"
        >
            <div className="container-mx relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="tag mb-8"
                >
                    <span>// beta · Польща та Україна</span>
                </motion.div>

                <h1 className="font-display font-extrabold text-text-primary leading-[0.95]" style={{ fontSize: "clamp(52px, 8vw, 96px)", letterSpacing: "-0.035em" }}>
                    <motion.span
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="block"
                    >
                        Ваша Google реклама
                    </motion.span>
                    <motion.span
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.3 }}
                        className="block mt-2"
                    >
                        <span className="strike-wrap text-text-secondary">витрачає гроші</span>{" "}
                        <motion.span
                            initial={{ opacity: 0, x: -16 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 2.0 }}
                            className="inline-block"
                            style={{
                                background: "linear-gradient(135deg, #F1F5F9 0%, #6D5EF8 60%, #2DD4BF 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                            }}
                        >
                            нарешті працює
                        </motion.span>
                    </motion.span>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.6 }}
                    className="mt-8 text-lg text-secondary-c max-w-[540px] leading-relaxed"
                >
                    AI аналізує ваш бізнес, будує кампанію і коригує її щотижня.
                    Ви просто дивитесь на результат.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.8 }}
                    className="mt-10 flex flex-wrap gap-4"
                >
                    <button onClick={() => navigate(ctaTarget)} className="btn-primary pulse-glow" data-testid="hero-cta-activate">
                        <Rocket size={18} /> {user ? "Перейти в кабінет →" : "Активувати AI →"}
                    </button>
                    <button onClick={scrollToHow} className="btn-ghost" data-testid="hero-cta-demo">
                        <Play size={16} /> Подивитись як це працює
                    </button>
                </motion.div>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 1.0 }}
                    className="mt-5 text-xs font-mono text-faint-c"
                >
                    Без контракту · Скасування в 1 клік
                </motion.p>
            </div>

            {/* Photo strip */}
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.0 }}
                className="container-mx mt-16 relative"
            >
                <div className="relative w-full rounded-2xl overflow-hidden" style={{ aspectRatio: "16/5" }}>
                    <img
                        src="https://images.unsplash.com/photo-1774274372921-194fb67dc3cc?auto=format&fit=crop&q=80&w=2400"
                        alt="Власник бізнесу"
                        className="w-full h-full object-cover"
                        loading="eager"
                    />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,15,0.2) 0%, rgba(10,10,15,0) 30%, rgba(10,10,15,0) 70%, rgba(10,10,15,0.95) 100%)" }} />
                </div>

                {/* Floating glass cards */}
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 1.4 }}
                    className="hidden md:flex items-center gap-2 glass px-4 py-3 rounded-xl absolute left-8 bottom-[-20px] md:bottom-8"
                    data-testid="hero-card-1"
                >
                    <span className="font-mono text-xs text-text-primary">📍 Краків, Польща · Сантехнік · +43 дзвінки/міс</span>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 1.6 }}
                    className="hidden md:flex items-center gap-2 glass px-4 py-3 rounded-xl absolute right-8 bottom-[-20px] md:bottom-8"
                    data-testid="hero-card-2"
                >
                    <span className="font-mono text-xs">
                        CPA: <span className="text-secondary-c">$8.40</span> → <span className="text-success-c">$3.20</span> за 3 тижні
                    </span>
                </motion.div>
            </motion.div>
        </section>
    );
};

export default Hero;
